import type { ReferenceBandGrid } from "./diagnosticsSeries";
import { axisSample, type EpisodeDiagnosticIndex } from "./episodeDiagnosticValues";

/**
 * 데이터셋 조망의 순수 계산. 다음 두 질문에 답하는 값을 만든다:
 * - 기준 밴드가 얼마나 좁은가
 * - 에피소드들이 어떻게 흩어져 있는가
 *
 * 채널 카드는 에피소드 하나를 채널·dim 으로 나눠 표시한다. 그래서 "이 데이터셋 기준선이 좁은가"
 * 와 "이 에피소드가 다른 에피소드 대비 어디인가" 에 답하지 못한다. 이 모듈이 그 두 값을 만든다.
 *
 * Owns:
 * - 밴드 폭 비율(무차원)과 기준 밖 비율 분포(히스토그램·중앙값).
 *
 * Does not own:
 * - 밴드·진단 행 적재 · 렌더 · 문구.
 */

/**
 * 기준 밴드가 값 범위에서 차지하는 비율(0~1). 작을수록 시연이 모여 있다.
 *
 * dim 하나의 폭 = median_t(high−low), 그 dim 의 값 범위 = max_t(high) − min_t(low) 다. 폭을 범위로
 * 나눠 **무차원**으로 만들어야 단위가 다른 채널을 한 값으로 묶을 수 있다. 데이터셋 값은 dim 별
 * 비율의 중앙값이다. 평균은 범위가 거의 0 인 dim(상수 채널)의 튀는 값 쪽으로 치우친다.
 *
 * 범위가 0 이거나 비유한인 dim 은 뺀다. 폭을 비교할 기준이 없기 때문이다. 남는 dim 이 없으면 null 이다.
 */
export function bandWidthRatio(band: ReferenceBandGrid): number | null {
  const ratios: number[] = [];
  for (const channel of band.channels.values()) {
    for (let d = 0; d < channel.low.length; d += 1) {
      const ratio = dimWidthRatio(channel.low[d] ?? [], channel.high[d] ?? []);
      if (ratio !== null) {
        ratios.push(ratio);
      }
    }
  }
  return median(ratios);
}

function dimWidthRatio(low: readonly number[], high: readonly number[]): number | null {
  const n = Math.min(low.length, high.length);
  const widths: number[] = [];
  let rangeMin = Number.POSITIVE_INFINITY;
  let rangeMax = Number.NEGATIVE_INFINITY;
  for (let t = 0; t < n; t += 1) {
    const lo = low[t];
    const hi = high[t];
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      continue;
    }
    widths.push(hi - lo);
    rangeMin = Math.min(rangeMin, lo);
    rangeMax = Math.max(rangeMax, hi);
  }
  const range = rangeMax - rangeMin;
  if (widths.length === 0 || !Number.isFinite(range) || range <= 0) {
    return null;
  }
  const width = median(widths);
  return width === null ? null : width / range;
}

/** 표본 중앙값(짝수 개면 가운데 두 값의 평균). 표본이 비면 null 이다. */
export function median(sample: readonly number[]): number | null {
  if (sample.length === 0) {
    return null;
  }
  const sorted = [...sample].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * 표본을 오름차순으로 정렬한 곡선. x 는 순위, y 는 값이다.
 *
 * 히스토그램이 아니라 정렬 곡선을 쓰는 이유는 다음과 같다. 이 지표(`outside_reference_fraction`)는
 * 값이 낮은 쪽에 몰린다. 값을 x 로 두면 오른쪽이 항상 비어 모양을 읽을 수 없다. 순위를 x 로
 * 두면 값이 치우쳐도 폭을 다 쓴다. 곡선이 완만한지, 끝에서 급하게 오르는지를 보면 "데이터셋이
 * 고른가" 를 알 수 있다. 형식은 arXiv 2605.01544 Fig.1 오른쪽과 같다.
 *
 * 비유한 값은 뺀다. 표본이 비면 빈 배열이다.
 */
export type SortedCurve = {
  // 오름차순 값. 인덱스가 순위(0-based)다.
  values: number[];
  min: number;
  max: number;
};

export function sortedCurve(sample: readonly number[]): SortedCurve | null {
  const values = sample.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (values.length === 0) {
    return null;
  }
  return { values, min: values[0], max: values[values.length - 1] };
}

/**
 * 곡선에서 이 값의 순위(0-based). 같은 값이 여럿이면 그 값들 중 첫 순위를 돌려준다. 동점을
 * 순서로 구분할 근거가 없으므로 모두 같은 순위로 본다. 값이 표본에 없으면 삽입 위치(하한)를 돌려준다.
 */
export function curveRank(curve: SortedCurve, value: number): number {
  let low = 0;
  let high = curve.values.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (curve.values[mid] < value) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

/** 이 에피소드의 기준 밖 비율과 전체 에피소드 중 순위. 요약 줄과 조망 곡선이 같은 값을 쓴다. */
export type DeviationPosition = {
  value: number;
  // 오름차순 0-based 순위.
  rank: number;
  total: number;
};

/**
 * 진단 행 색인에서 이 에피소드의 기준 밖 비율 순위를 찾는다. 값이 없거나(밴드 미산출·표본
 * 부족) 표본이 비면 null 이다. "0번째" 와 "잴 수 없음" 을 구분한다.
 */
export function deviationPosition(
  index: EpisodeDiagnosticIndex,
  episodeUuid: string,
): DeviationPosition | null {
  const value = index.get(episodeUuid)?.outsideReferenceFraction ?? null;
  const curve = sortedCurve(axisSample(index, "outsideReferenceFraction"));
  if (value === null || curve === null) {
    return null;
  }
  return { value, rank: curveRank(curve, value), total: curve.values.length };
}
